/**
 * wayai contacts — a contact's customer record in one of the organization's bases (#5083 RK2 A3), and the
 * organization's person links and deny list (wayai-pro/wayai#5473 §1).
 *
 * Usage:
 *   wayai contacts link-customer <contact_id> --base <base_id> --record <record_id> [--org <org_id>] [--json]
 *   wayai contacts unlink-customer <contact_id> --base <base_id> [--org <org_id>] [--json]
 *   wayai contacts lookup-person <person_id> [--org <org_id>] [--json]
 *   wayai contacts person-links <person_id> [--org <org_id>] [--json]
 *   wayai contacts link-person <person_id> --to <person_id|contact_id> [--org <org_id>] [--json]
 *   wayai contacts unlink-person <person_id> --to <person_id|contact_id> [--org <org_id>] [--json]
 *   wayai contacts denied-persons [--cursor <id>] [--limit <n>] [--org <org_id>] [--json]
 *   wayai contacts deny-person <person_id|contact_id> [--org <org_id>] [--json]
 *   wayai contacts undeny-person <person_id|contact_id> [--org <org_id>] [--json]
 *
 * Every one goes to an `/api/contact-book` route that only a person signed in through the browser
 * (`wayai login`) reaches — an API token is refused, and the message here says so. A refusal's message is
 * the route's, sanitized.
 */

import { parseArgs } from 'node:util';
import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { extractApiMessage } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { readRepoConfigUnlessBlocked } from '../lib/repo-config.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { envTokenOverride, identifyTokenType } from '../lib/token-store.js';
import { BASE_ID_RULE, isValidBaseId } from '../lib/base-id.js';
import { UUID_RE, wantsHelp } from '../lib/utils.js';
import {
  PERSON_DENY_PAGE_MAX,
  PERSON_ID_FORM_MESSAGE,
  personIdSchema,
  userPersonIdSchema,
  type PersonLinkEntry,
} from '@wayai/core/contracts';

const USAGE = `
wayai contacts — a contact's customer record in a base, and the organization's person links and deny list

Usage:
  wayai contacts link-customer <contact_id> --base <base_id> --record <record_id> [--org <org_id>] [--json]
  wayai contacts unlink-customer <contact_id> --base <base_id> [--org <org_id>] [--json]
  wayai contacts lookup-person <person_id> [--org <org_id>] [--json]
  wayai contacts person-links <person_id> [--org <org_id>] [--json]
  wayai contacts link-person <person_id> --to <person_id|contact_id> [--org <org_id>] [--json]
  wayai contacts unlink-person <person_id> --to <person_id|contact_id> [--org <org_id>] [--json]
  wayai contacts denied-persons [--cursor <id>] [--limit <n>] [--org <org_id>] [--json]
  wayai contacts deny-person <person_id|contact_id> [--org <org_id>] [--json]
  wayai contacts undeny-person <person_id|contact_id> [--org <org_id>] [--json]

link-customer    Make the record the contact's customer record: an eval declaring the contact
                 then reaches that customer through a customer-scoped toolset (an agent's and
                 a customer page's calls name no customer). The record is one of the base's
                 customer record type (x-customer-scope role root), holding no external key
                 or the contact id under another source. A record keyed under another
                 external_id is refused: re-keying it would orphan its owned records.
unlink-customer  Unlink the contact's customer record in the base. The record keeps the
                 contact id under the source wayai_contact_unlinked, so its owned records stay
                 its own, and link-customer on the same record links it again.

lookup-person    Whom a person id (prs_…) names: a hub of the organization where a
                 conversation carries it, and that hub's name and email for the person. A
                 person id no conversation carries is unknown.
person-links     The person ids and contacts a person is linked to, oldest first.
link-person      Link a person to another person id (their earlier identity, say an old
                 number) or to a contact of the organization: the person is also that one.
                 The target's own links are copied too. A person holds at most 16 links.
unlink-person    Remove one link. The links it copied stay.
denied-persons   The organization's deny list: person ids and contacts no person set holds.
deny-person      Deny a person id or a contact for the organization.
undeny-person    Lift a denial.

Person links and the deny list give and take no access yet: no call reads them.

The organization is --org, else this repo's (wayai-ws/wayai.yaml). Linking and unlinking
a person needs org:write over the organization; everything else here an organization
admin. These commands need a browser sign-in (\`wayai login\`): an API token
(\`wayai login --token\`, or WAYAI_TOKEN) is refused.
`.trim();

const SUBCOMMANDS = [
  'link-customer',
  'unlink-customer',
  'lookup-person',
  'person-links',
  'link-person',
  'unlink-person',
  'denied-persons',
  'deny-person',
  'undeny-person',
] as const;
type Subcommand = (typeof SUBCOMMANDS)[number];

function usageError(message: string): Error {
  return expected(`${message}\n\n${USAGE}`);
}

function parse(args: string[]) {
  try {
    return parseArgs({
      args,
      options: {
        org: { type: 'string' },
        base: { type: 'string' },
        record: { type: 'string' },
        to: { type: 'string' },
        cursor: { type: 'string' },
        limit: { type: 'string' },
        json: { type: 'boolean' },
      },
      allowPositionals: true,
      strict: true,
    });
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

type Values = ReturnType<typeof parse>['values'];

/** The org from `--org`, else the repo's binding; refused when neither names one. */
function resolveOrgId(flag: string | undefined): string {
  if (flag !== undefined) {
    if (!UUID_RE.test(flag)) throw expected(`Invalid --org ${JSON.stringify(flag)}: expected an organization id (a UUID).`);
    return flag;
  }
  const bound = readRepoConfigUnlessBlocked()?.organization_id;
  if (bound) return bound;
  throw expected('No organization: pass --org <org_id>, or run this inside a workspace scoped with `wayai init`.');
}

/**
 * A refusal as a message naming the next step: for an API token's 403, the browser sign-in (these
 * routes take a person only); else the route's own message.
 */
function failure(err: unknown, accessToken: string): unknown {
  if (!(err instanceof ApiError)) return err;
  if (err.status === 403 && identifyTokenType(accessToken) === 'way_token') {
    const override = envTokenOverride()
      ? ' WAYAI_TOKEN is set in this shell and is sent instead of your sign-in: unset it first.'
      : '';
    return expected(
      'These commands work from a browser sign-in only, and this login is an API token. ' +
        `Run \`wayai login\` (without --token).${override}`,
    );
  }
  if (err.status >= 400 && err.status < 500) return expected(sanitizeTerminalText(extractApiMessage(err)));
  return err;
}

const text = (value: string): string => sanitizeTerminalText(value);

/** A user's person id, refused before any request otherwise. */
function userPersonId(sub: string, value: string | undefined): string {
  if (value === undefined) throw usageError(`${sub} takes one <person_id>.`);
  if (!userPersonIdSchema.safeParse(value).success) throw usageError(`Invalid <person_id> ${JSON.stringify(value)}: expected a person id (prs_…).`);
  return value;
}

/** A person id or a contact id, refused before any request otherwise. */
function personOrContactId(what: string, value: string | undefined): string {
  if (value === undefined) throw usageError(`Missing ${what}.`);
  if (!personIdSchema.safeParse(value).success) {
    throw usageError(`Invalid ${what} ${JSON.stringify(value)}. ${PERSON_ID_FORM_MESSAGE}.`);
  }
  return value;
}

/** Refuse a flag the subcommand does not take, so a mistyped command never runs as another. */
function refuseFlags(sub: string, values: Values, flags: readonly (keyof Values)[]): void {
  for (const flag of flags) {
    if (values[flag] !== undefined) throw usageError(`--${String(flag)} does not apply to ${sub}.`);
  }
}

function entryLine(entry: PersonLinkEntry): string {
  return `  ${text(entry.person_id)}  since ${text(entry.created_at)}, by ${text(entry.created_by)}`;
}

function print(values: Values, data: unknown, lines: string[]): void {
  console.log(values.json ? JSON.stringify(data, null, 2) : lines.join('\n'));
}

/** link-customer / unlink-customer: the contact's customer record (#5083 RK2 A3). */
async function customerRecord(sub: 'link-customer' | 'unlink-customer', positionals: string[], values: Values): Promise<void> {
  const [contactId, ...extra] = positionals;
  if (contactId === undefined || extra.length > 0) throw usageError(`${sub} takes one <contact_id>.`);
  if (!UUID_RE.test(contactId)) throw usageError(`Invalid <contact_id> ${JSON.stringify(contactId)}: expected a contact id (a UUID).`);
  refuseFlags(sub, values, ['to', 'cursor', 'limit']);
  if (!values.base) throw usageError(`${sub} needs --base <base_id>.`);
  if (!isValidBaseId(values.base)) throw usageError(`Invalid --base ${JSON.stringify(values.base)}: a base id is ${BASE_ID_RULE}.`);
  if (sub === 'link-customer' && !values.record) throw usageError('link-customer needs --record <record_id>.');
  if (sub === 'unlink-customer' && values.record !== undefined) throw usageError('--record applies only to link-customer.');
  const orgId = resolveOrgId(values.org);

  await withApi(async (api) => {
    const { customer } = sub === 'link-customer'
      ? await api.linkContactCustomer(orgId, contactId, { base_id: values.base!, record_id: values.record! })
      : await api.unlinkContactCustomer(orgId, contactId, values.base!);
    const where = `base ${text(customer.base_id)} (${text(customer.record_type)})`;
    print(values, customer, [sub === 'link-customer'
      ? `Contact ${customer.contact_id} is linked to customer record ${text(customer.record_id)} in ${where}.`
      : `Contact ${customer.contact_id} is unlinked from customer record ${text(customer.record_id)} in ${where}; ` +
        `the record keeps the contact id under ${customer.external_source}.`]);
  });
}

/** The person-link and deny-list subcommands (wayai-pro/wayai#5473 §1). */
async function persons(sub: Exclude<Subcommand, 'link-customer' | 'unlink-customer'>, positionals: string[], values: Values): Promise<void> {
  refuseFlags(sub, values, ['base', 'record']);
  const takesTo = sub === 'link-person' || sub === 'unlink-person';
  if (!takesTo) refuseFlags(sub, values, ['to']);
  if (sub !== 'denied-persons') refuseFlags(sub, values, ['cursor', 'limit']);
  const [first, ...extra] = positionals;
  if (sub === 'denied-persons' ? positionals.length > 0 : extra.length > 0) {
    throw usageError(sub === 'denied-persons' ? 'denied-persons takes no arguments.' : `${sub} takes one id.`);
  }
  let limit: number | undefined;
  if (values.limit !== undefined) {
    limit = Number(values.limit);
    if (!Number.isInteger(limit) || limit < 1 || limit > PERSON_DENY_PAGE_MAX) {
      throw usageError(`Invalid --limit ${JSON.stringify(values.limit)}: expected a whole number from 1 to ${PERSON_DENY_PAGE_MAX}.`);
    }
  }
  const cursor = values.cursor === undefined ? undefined : personOrContactId('--cursor', values.cursor);
  const subject = sub === 'denied-persons' ? '' : sub === 'deny-person' || sub === 'undeny-person'
    ? personOrContactId('<person_id|contact_id>', first)
    : userPersonId(sub, first);
  const target = takesTo ? personOrContactId('--to <person_id|contact_id>', values.to) : '';
  const orgId = resolveOrgId(values.org);

  await withApi(async (api) => {
    switch (sub) {
      case 'lookup-person': {
        const { person } = await api.lookupPerson(orgId, subject);
        print(values, person, [person.status === 'found'
          ? `Person ${person.person_id} is ${person.user_name ? text(person.user_name) : 'unnamed'}` +
            `${person.user_email ? ` <${text(person.user_email)}>` : ''}, ` +
            `in hub ${person.hub_name ? text(person.hub_name) : 'unnamed'} (${text(person.hub_id)}).`
          : `No conversation of the organization's hubs carries person ${person.person_id}.`]);
        return;
      }
      case 'person-links': {
        const data = await api.listPersonLinks(orgId, subject);
        print(values, data, data.links.length === 0
          ? [`Person ${data.person_id} has no person links.`]
          : [`Person ${data.person_id} is linked to:`, ...data.links.map(entryLine)]);
        return;
      }
      case 'link-person': {
        const data = await api.linkPerson(orgId, subject, target);
        print(values, data, [data.status === 'linked'
          ? `Person ${data.person_id} is linked to ${target}${data.added ? `; added ${data.added.map(text).join(', ')}` : ''}.`
          : `Person ${data.person_id} was already linked to ${target} and its own links.`]);
        return;
      }
      case 'unlink-person': {
        const data = await api.unlinkPerson(orgId, subject, target);
        print(values, data, [`Person ${subject} is no longer linked to ${target}.`]);
        return;
      }
      case 'denied-persons': {
        const data = await api.listDeniedPersons(orgId, { cursor, limit });
        const lines = data.denied.length === 0 ? ['Nothing is denied.'] : ['Denied:', ...data.denied.map(entryLine)];
        if (data.next_cursor) lines.push(`More: --cursor ${text(data.next_cursor)}`);
        print(values, data, lines);
        return;
      }
      case 'deny-person': {
        const data = await api.denyPerson(orgId, subject);
        print(values, data, [data.status === 'denied' ? `Denied ${data.person_id}.` : `${data.person_id} was already denied.`]);
        return;
      }
      case 'undeny-person': {
        const data = await api.undenyPerson(orgId, subject);
        print(values, data, [`Lifted the denial of ${subject}.`]);
        return;
      }
    }
  });
}

/** Run `act` with a client of the signed-in session; a refusal comes back as `failure` words it. */
async function withApi(act: (api: ApiClient) => Promise<void>): Promise<void> {
  const { config, accessToken } = await requireAuth();
  // A browser login is a JWT that expires; refresh it on a 401 instead of failing.
  const api = new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });
  try {
    await act(api);
  } catch (err) {
    throw failure(err, accessToken);
  }
}

export async function contactsCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const { values, positionals } = parse(args);
  const [sub, ...rest] = positionals;
  if (!SUBCOMMANDS.includes(sub as Subcommand)) throw usageError(`Unknown subcommand ${JSON.stringify(sub)}.`);
  const command = sub as Subcommand;
  if (command === 'link-customer' || command === 'unlink-customer') return customerRecord(command, rest, values);
  return persons(command, rest, values);
}
